/**
 * Session and time overlay math (feature bible B3, owner 2026-09-30): the named session opens
 * (midnight New York, the 18:00 ET Globex open, the 09:30 ET RTH open) and the editable killzones,
 * as pure functions over UTC epoch milliseconds so the chart layer and the tests share one meaning.
 *
 * - Times are **New York wall clock** (`America/New_York`), DST-correct with the same integer rule
 *   as `calendar.ts` (no tz database): New York changes its clocks at 02:00 local, which is one
 *   hour of UTC *before* Chicago does (07:00Z in March, 06:00Z in November).
 * - Wall time → instant uses the calendar's DST rule: a time in the spring-forward gap resolves
 *   one hour forward, a time in the repeated fall-back hour resolves to the earlier (EDT) instant.
 *   Neither occurs on a trading session (both fall on Sunday morning, while Globex is closed).
 * - Exchange holidays and early closes are **not modelled** (as in `calendar.ts`); the chart only
 *   draws a mark or a zone where bars exist, so a closed day shows nothing rather than a guess.
 * - Market-data annotations only: nothing here touches orders, accounts or money.
 */
import { civilFromDays, dstEndMs, dstStartMs, tradingDayOpenMs, tradingDayOf, weekday } from "./calendar.ts";

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 86_400_000;
const EST = -5 * HOUR;
const EDT = -4 * HOUR;

// ------------------------------------------------------------------------------ New York time

/** Offset of New York time from UTC at a UTC instant: −4 h (EDT) or −5 h (EST), in ms. */
export function easternOffsetMs(utcMs: number): number {
  const [year] = civilFromDays(Math.floor(utcMs / DAY));
  // Chicago switches at 02:00 local = 08:00Z / 07:00Z; New York one hour earlier in UTC.
  return utcMs >= dstStartMs(year) - HOUR && utcMs < dstEndMs(year) - HOUR ? EDT : EST;
}

/**
 * New York civil day (days since 1970-01-01) + minute of day → UTC ms. The nonexistent
 * spring-forward hour resolves one hour forward (02:30 → 03:30 EDT); the repeated fall-back hour
 * resolves to the earlier (EDT) instant.
 */
export function easternToUtcMs(day: number, minuteOfDay: number): number {
  const local = day * DAY + minuteOfDay * MIN;
  const asEdt = local - EDT;
  return easternOffsetMs(asEdt) === EDT ? asEdt : local - EST;
}

/** New York civil day (days since 1970-01-01) of a UTC instant. */
export function easternDayOf(utcMs: number): number {
  return Math.floor((utcMs + easternOffsetMs(utcMs)) / DAY);
}

// ----------------------------------------------------------------------------- session opens

export type SessionOpenKind = "midnight" | "globex" | "rth";

export const SESSION_OPEN_KINDS: readonly SessionOpenKind[] = ["midnight", "globex", "rth"];

/** New York minute of day of each named open. */
export const SESSION_OPEN_MINUTE_ET: Readonly<Record<SessionOpenKind, number>> = {
  midnight: 0,
  globex: 18 * 60,
  rth: 9 * 60 + 30,
};

/** Short chart labels (vertical line tops) and longer names (settings). */
export const SESSION_OPEN_LABEL: Readonly<Record<SessionOpenKind, string>> = {
  midnight: "NY 00:00",
  globex: "Globex 18:00",
  rth: "RTH 09:30",
};

export const SESSION_OPEN_NAME: Readonly<Record<SessionOpenKind, string>> = {
  midnight: "Midnight New York open (00:00 ET)",
  globex: "Globex open (18:00 ET)",
  rth: "RTH open (09:30 ET)",
};

export type SessionMark = { kind: SessionOpenKind; ts: number };

/**
 * The named opens in `[fromMs, toMs]`, oldest first (equal instants: midnight, globex, rth):
 *
 * - **midnight**: 00:00 New York on Monday–Friday (Saturday 00:00 is after the Friday close);
 * - **globex**: the CME trading-day open, 17:00 CT = 18:00 ET, Sunday–Thursday (the Sunday open
 *   starts Monday's trading day). Taken from `calendar.ts` so it always matches the session breaks;
 * - **rth**: 09:30 New York (the 08:30 CT equity-index RTH open), Monday–Friday.
 *
 * Holidays are not modelled; a caller drawing marks requires a bar at the instant.
 */
export function sessionOpens(
  fromMs: number,
  toMs: number,
  kinds: Readonly<Record<SessionOpenKind, boolean>>,
): SessionMark[] {
  const out: SessionMark[] = [];
  if (!(toMs >= fromMs) || !Number.isFinite(fromMs) || !Number.isFinite(toMs)) return out;
  const d0 = easternDayOf(fromMs) - 1;
  const d1 = easternDayOf(toMs) + 1;
  for (let d = d0; d <= d1; d++) {
    const wd = weekday(d);
    const weekdayMonFri = wd >= 1 && wd <= 5;
    const push = (kind: SessionOpenKind, ts: number): void => {
      if (ts >= fromMs && ts <= toMs) out.push({ kind, ts });
    };
    if (kinds.midnight && weekdayMonFri) push("midnight", easternToUtcMs(d, SESSION_OPEN_MINUTE_ET.midnight));
    if (kinds.rth && weekdayMonFri) push("rth", easternToUtcMs(d, SESSION_OPEN_MINUTE_ET.rth));
    if (kinds.globex && wd !== 5 && wd !== 6) {
      // The trading day that opens this evening (Sunday → Monday's).
      const ts = easternToUtcMs(d, SESSION_OPEN_MINUTE_ET.globex);
      push("globex", tradingDayOpenMs(tradingDayOf(ts + MIN)));
    }
  }
  const order: Record<SessionOpenKind, number> = { midnight: 0, globex: 1, rth: 2 };
  return out.sort((a, b) => a.ts - b.ts || order[a.kind] - order[b.kind]);
}

// --------------------------------------------------------------------------------- killzones

/** Chart theme colour tokens a killzone may use (`@helm/chart` ChartTheme keys). */
export type KillzoneColor = "accent" | "up" | "down" | "last" | "text" | "textDim";

export const KILLZONE_COLORS: readonly KillzoneColor[] = ["accent", "up", "down", "last", "text", "textDim"];

/** A user-editable time band in New York time. `end` ≤ `start` crosses midnight. */
export type Killzone = {
  id: string;
  name: string;
  /** "HH:MM", New York time. */
  start: string;
  end: string;
  on: boolean;
  color: KillzoneColor;
};

/** Owner defaults (feature-bible B3). */
export const DEFAULT_KILLZONES: readonly Killzone[] = [
  { id: "asia", name: "Asia", start: "20:00", end: "00:00", on: true, color: "accent" },
  { id: "london", name: "London", start: "02:00", end: "05:00", on: true, color: "up" },
  { id: "nyam", name: "NY AM", start: "08:00", end: "11:00", on: true, color: "last" },
  { id: "londonClose", name: "London close", start: "10:00", end: "12:00", on: true, color: "textDim" },
  { id: "nypm", name: "NY PM", start: "13:30", end: "16:00", on: true, color: "down" },
];

/** Max killzones kept per pane, and the longest name. */
export const MAX_KILLZONES = 8;
export const MAX_KILLZONE_NAME = 24;
/** Longest span (days) killzones are computed over; a wider view draws none. */
export const MAX_KILLZONE_DAYS = 400;

/** "HH:MM" (00:00–23:59) → minute of day, or null. */
export function parseHhmm(s: unknown): number | null {
  if (typeof s !== "string") return null;
  const m = /^(\d{2}):(\d{2})$/.exec(s);
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  return h <= 23 && mi <= 59 ? h * 60 + mi : null;
}

/** One occurrence of killzone `zone` (index into the list), UTC ms `[start, end)`. */
export type KillzoneInterval = { zone: number; start: number; end: number };

/**
 * Occurrences of the enabled killzones overlapping `[fromMs, toMs)`, oldest first. Each occurrence
 * is the zone's New York wall-clock times on one New York date (a zone whose end is not after its
 * start ends the next day), so its length follows the clock across a DST change. Invalid zones
 * (bad time, start = end) are skipped; spans over `MAX_KILLZONE_DAYS` return nothing.
 */
export function killzoneIntervals(
  zones: readonly Killzone[],
  fromMs: number,
  toMs: number,
): KillzoneInterval[] {
  const out: KillzoneInterval[] = [];
  if (!(toMs > fromMs) || !Number.isFinite(fromMs) || !Number.isFinite(toMs)) return out;
  if (toMs - fromMs > MAX_KILLZONE_DAYS * DAY) return out;
  const parsed = zones.map((z) => {
    const s = parseHhmm(z.start);
    const e = parseHhmm(z.end);
    return z.on && s !== null && e !== null && s !== e ? { s, e } : null;
  });
  const d0 = easternDayOf(fromMs) - 1;
  const d1 = easternDayOf(toMs);
  for (let d = d0; d <= d1; d++) {
    parsed.forEach((p, zone) => {
      if (!p) return;
      const start = easternToUtcMs(d, p.s);
      const end = p.e > p.s ? easternToUtcMs(d, p.e) : easternToUtcMs(d + 1, p.e);
      if (end > fromMs && start < toMs) out.push({ zone, start, end });
    });
  }
  return out.sort((a, b) => a.start - b.start || a.zone - b.zone);
}

// ------------------------------------------------------------------------------ pane settings

/**
 * Per-pane session overlay switches, stored as `overlays.session` in
 * `chartLayout.panes[i].overlays`. Absent (every layout saved before B3) = everything off.
 */
export type SessionOverlay = {
  /** Prior trading day's close (last bar's close of that day, from bars; not the settlement). */
  pdc: boolean;
  midnight: boolean;
  globex: boolean;
  rth: boolean;
  /** Master switch for the killzone bands (each zone also has its own `on`). */
  killzones: boolean;
  zones: Killzone[];
};

export const DEFAULT_SESSION_OVERLAY: SessionOverlay = {
  pdc: false,
  midnight: false,
  globex: false,
  rth: false,
  killzones: false,
  zones: DEFAULT_KILLZONES.map((z) => ({ ...z })),
};

/** True when any session overlay would draw (the layers' early-out). */
export function sessionOverlayActive(s: SessionOverlay | null | undefined): boolean {
  return !!s && (s.pdc || s.midnight || s.globex || s.rth || (s.killzones && s.zones.some((z) => z.on)));
}

function sanitizeZone(raw: unknown, i: number): Killzone | null {
  if (typeof raw !== "object" || raw === null) return null;
  const r = raw as Record<string, unknown>;
  const s = parseHhmm(r.start);
  const e = parseHhmm(r.end);
  if (s === null || e === null || s === e) return null;
  const id = typeof r.id === "string" && r.id.length > 0 && r.id.length <= 32 ? r.id : `zone${String(i + 1)}`;
  const name =
    typeof r.name === "string" && r.name.trim().length > 0
      ? r.name.trim().slice(0, MAX_KILLZONE_NAME)
      : `Zone ${String(i + 1)}`;
  const color = KILLZONE_COLORS.includes(r.color as KillzoneColor) ? (r.color as KillzoneColor) : "accent";
  return { id, name, start: r.start as string, end: r.end as string, on: r.on !== false, color };
}

/**
 * Stored (untrusted, possibly partial) session settings → full settings. Missing switches are off;
 * a missing or non-array zone list takes the defaults; invalid zones are dropped, duplicate ids
 * keep the first.
 */
export function sanitizeSessionOverlay(raw: unknown): SessionOverlay {
  const base = DEFAULT_SESSION_OVERLAY;
  if (typeof raw !== "object" || raw === null) return { ...base, zones: base.zones.map((z) => ({ ...z })) };
  const r = raw as Record<string, unknown>;
  const bool = (k: keyof SessionOverlay): boolean => r[k] === true;
  let zones: Killzone[];
  if (Array.isArray(r.zones)) {
    zones = [];
    const seen = new Set<string>();
    for (const [i, z] of r.zones.slice(0, MAX_KILLZONES).entries()) {
      const k = sanitizeZone(z, i);
      if (!k || seen.has(k.id)) continue;
      seen.add(k.id);
      zones.push(k);
    }
  } else zones = base.zones.map((z) => ({ ...z }));
  return {
    pdc: bool("pdc"),
    midnight: bool("midnight"),
    globex: bool("globex"),
    rth: bool("rth"),
    killzones: bool("killzones"),
    zones,
  };
}
